import React from 'react';
import { KFButton } from '@kf/ic2fx';
import { Typography } from '@mui/material';
import { colors } from 'styles/colors';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import RightTickIcon from 'shared/icons/RightTickIcon';
import MenuCloseIcon from 'shared/icons/MenuCloseIcon';
import UnsureIcon from 'shared/icons/UnsureIcon';
import MenuCloseDisableIcon from 'shared/icons/MenuCloseIconDisable';
import RightTickDisableIcon from 'shared/icons/RightTickDisableIcon';

interface IFeedbackButtonProps {
  id: string;
  variant: 'recommends' | 'doesntRecommend' | 'notSure';
  count?: number;
  handleClick?: (event: string) => void;
  buttonSx?: React.CSSProperties;
  isDisabled?: boolean;
  height?: string;
  width?: string;
}

interface IIconStyles {
  width?: string;
  height?: string;
  flexShrink?: number;
  marginLeft?: string;
  [key: string]: string | number | undefined;
}

const getVariantStyles = (
  variant: 'recommends' | 'doesntRecommend' | 'notSure',
  isDisabled: boolean | undefined
) => {
  const variantStyles = {
    recommends: {
      labelKey: 'recommends',
      borderColor: colors.tertiary.green,
      color: colors.type.copy,
      icon: (styles: IIconStyles) =>
        isDisabled ? (
          <RightTickDisableIcon
            sx={{ ...styles, marginLeft: '0 !important' }}
          />
        ) : (
          <RightTickIcon sx={{ ...styles, marginLeft: '0 !important' }} />
        ),
    },
    doesntRecommend: {
      labelKey: 'doesntRecommend',
      borderColor: colors.tertiary.cadimiumRed,
      color: colors.type.copy,
      icon: (styles: IIconStyles) =>
        isDisabled ? (
          <MenuCloseDisableIcon
            sx={{ ...styles, marginLeft: '0 !important' }}
          />
        ) : (
          <MenuCloseIcon sx={{ ...styles, marginLeft: '0 !important' }} />
        ),
    },
    notSure: {
      labelKey: 'unsure',
      borderColor: colors.tertiary.darkGray,
      color: `${colors.tertiary.darkGray} !important`,
      icon: (styles: IIconStyles) => (
        <UnsureIcon sx={{ ...styles, marginLeft: '0 !important' }} />
      ),
    },
  };

  return isDisabled ? variantStyles[variant] : variantStyles[variant];
};

const FeedbackButton: React.FC<IFeedbackButtonProps> = ({
  variant,
  count,
  buttonSx,
  id,
  handleClick,
  isDisabled,
  height = '2.25rem',
  width,
}) => {
  const currentVariantStyles = getVariantStyles(variant, isDisabled);
  const shouldRemoveMarginLeft = buttonSx?.marginLeft === 0;

  const iconStyles = {
    width: '1.5rem',
    height: '1.5rem',
    flexShrink: 0,
  };

  return (
    <KFButton
      muiButtonProps={{
        sx: {
          borderRadius: '1rem !important',
          border: `0.0625rem solid ${currentVariantStyles.borderColor}`,
          cursor: 'pointer',
          color: currentVariantStyles.color,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: colors.background.componentBackground,
          fontWeight: '700',
          height: ` ${height} !important`,
          '&.Mui-disabled': {
            background: `${colors.background.midGray} !important`,
            borderColor: `${colors.tertiary.darkGray} !important`,
            color: colors.tertiary.darkGray,
            opacity: 0.5,
          },
          '&:hover': {
            backgroundColor: colors.background.componentBackground,
          },
          padding: '0.375rem 0.75rem',
          width: `${width}`,
          ...buttonSx,
        },
      }}
      icon={currentVariantStyles.icon(iconStyles)}
      id={id}
      value={id}
      data-testid={id}
      onClick={() => handleClick && handleClick(id)}
      disabled={isDisabled}
      disableTouchRipple
    >
      {count !== undefined && count !== null && (
        <Typography
          className='kfic2-bodyTextSmall'
          sx={{
            color: currentVariantStyles.color,
            fontWeight: 700,
          }}
        >
          {count}
        </Typography>
      )}
      <TypographyWithI18N
        className='kfic2-bodyTextSmall'
        i18nKey={currentVariantStyles.labelKey}
        sx={{
          color: currentVariantStyles.color,
          fontWeight: 700,
          ml: shouldRemoveMarginLeft ? 0 : '0.3125rem',
        }}
      />
    </KFButton>
  );
};

export default FeedbackButton;
